import { useEffect, useRef, useState } from "react";
import {
  Comparison,
  ContainerData,
  ContainerFolderStructure,
  ContainersList,
  Field,
  FilterField,
  RequestDataQuery,
} from "../../api/data-types";
import { Col, Row } from "antd";
import { FileBrowsingTable } from "../layout/file-browser/file-browser-table";
import { StorageAPIClient } from "../../api/storage-api-client";
import {
  getContainerData,
  getContainerList,
  limit_options,
} from "../browse-page/util/browse-page-util";
import { MetadataSearch } from "./sub-components/search-types/metadata-search";
import { GdriveSearch } from "./sub-components/search-types/gdrive-search";
import { FolderPatternSearch } from "./sub-components/search-types/folder-pattern-search";
import "./search-page.css";
import { MetadataPreview } from "../layout/metadata-preview/metadata-preview";
import { emptyRequestDataProperties as emptyData } from "../../api/data-types";
import {
  LatestInputState,
  emptyLatestInputState,
  getFolderPatternInputValue,
  getGdriveInputValue,
  getMetadataInputValue,
  updateStoredInputData,
} from "./util/data-types/input-state";
import { DropdownListSelection } from "../layout/list-selection-dropdown/list-selection-dropdown";
import { PagingStack } from "../browse-page/util/data-types/data-types-browse-page";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchPageSearchParameters } from "./util/data-types/search-params";
import {
  avaiableSearchTypes,
  removeSearchTypeValueSearchParams,
  updateRequestDataContainer,
  updateRequestDataLimit,
  updateUrlWithSelectedSearch,
  wasChangeSignificant,
} from "./util/search-page-util";
import { UserBrowsingSessionProps } from "../../App";
import { File } from "../../api/data-types";
import { Overlay } from "../overlay/overlay";
import { SizeWithUnit } from "../layout/file-browser/file-browser-table";
import dayjs from "dayjs";
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js";
import { isLocalHost } from "../../util/helpers";
import { ClientApiDiagnosticEvents } from "../../util/diagnosticEvent";

const UNDEFINED_SEARCH_DATA = undefined;
const DEFAULT_LIMIT = 100;
const DEFAULT_CONTAINER_LABEL = "";
const DEFAULT_SEARCH_LABEL = "Select search type";

export const SearchPage = ({
  theme,
  containerState,
  updateContainerState,
}: UserBrowsingSessionProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [containerData, setContainerData] = useState<ContainersList>({
    containers: [],
  });

  const [containerWriteData, setContainerWriteData] = useState<ContainersList>({
    containers: [],
  });

  const [containerWriteAccess, setContainerWriteAccess] = useState<boolean>();

  const [selectedContainer, setSelectedContainer] = useState<ContainerData>(
    new ContainerData(DEFAULT_CONTAINER_LABEL, "", [])
  );

  const [selectedLimit, setSelectedLimit] = useState<number>(DEFAULT_LIMIT);

  const [loadingComponent, setLoadingComponent] = useState<boolean>(false);

  const [sortField, setSortField] = useState<Field>(Field.FileName);

  const [filterFields, setFilterFields] = useState<FilterField[]>([
    new FilterField(Field.FileName, Comparison.Like, ""),
    new FilterField(Field.FolderName, Comparison.Like, ""),
    new FilterField(Field.FileSize, Comparison.Equals, ""),
    new FilterField(Field.FileSize, Comparison.Equals, ""),
    new FilterField(Field.ModificationDate, Comparison.Equals, ""),
    new FilterField(Field.ModificationDate, Comparison.Equals, ""),
  ]);

  const [sizesWithUnit, setSizesWithUnit] = useState<SizeWithUnit[]>([
    { size: 0, unit: "B" },
    { size: 0, unit: "B" },
  ]);

  const [filterFieldsForQuery, setFilterFieldsForQuery] = useState<
    FilterField[]
  >([]);

  const [isAscending, setIsAscending] = useState<boolean>(true);

  const [forceReload, setForceReload] = useState<boolean>(false);

  const currentUrlSearchParameters = new SearchPageSearchParameters(
    emptyData.EMPTY_CONTAINER,
    "search-type-not-selected",
    UNDEFINED_SEARCH_DATA,
    UNDEFINED_SEARCH_DATA,
    UNDEFINED_SEARCH_DATA,
    selectedLimit.toString()
  );

  const [requestDataQuerry, setRequestDataQuerry] = useState<RequestDataQuery>(
    new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      emptyData.EMPTY_CONTAINER,
      emptyData.EMPTY_PARENT_FOLDER,
      emptyData.EMPTY_OFFSET,
      emptyData.EMPTY_OFFSET,
      emptyData.EMPTY_OFFSET,
      selectedLimit,
      false
    )
  );

  const [latestSearchInput, setLatestSearchInput] = useState<LatestInputState>(
    emptyLatestInputState
  );

  const [selectedSearchType, setSelectedSearchType] =
    useState<string>(DEFAULT_SEARCH_LABEL);

  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);

  const [isSearchDone, setIsSearchDone] = useState<boolean>(false);

  const [searching, setSearching] = useState<boolean>(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const updateContainer = async (newContainer: string) => {
    const container = await getContainerData(newContainer);
    setSelectedContainer(container);
    updateContainerState(newContainer);
    return container;
  };

  const [currentPagingStack, setCurrentPagingStack] = useState<PagingStack>(
    new PagingStack([])
  );

  const updateSetLatestSearchInput = (input: LatestInputState) => {
    setLatestSearchInput(input);
  };

  const updateSelectedSearchType = (aSearchType: string) => {
    setSelectedSearchType(aSearchType);
  };

  const handleContainersDropdownMenuClick = async (containerName: string) => {
    updateContainer(containerName);
    setRequestDataQuerry((prevData) =>
      updateRequestDataContainer(prevData, containerName)
    );
    setIsSearchDone(false);
    resetRowSelection();
    setCurrentPagingStack(new PagingStack([]));
  };

  const updateSortField = (
    sortField: Field,
    filterFields: FilterField[],
    isAscending: boolean,
    sizesWithUnit: SizeWithUnit[]
  ) => {
    setSizesWithUnit(sizesWithUnit);
    setFilterFields(filterFields);
    setSortField(sortField);
    setIsAscending(isAscending);
    let newFilterFields: FilterField[] = [];
    for (let i = 0; i < filterFields.length; i++) {
      if (filterFields[i].valueOrPattern !== "") {
        newFilterFields = [...newFilterFields, filterFields[i]];
      }
    }
    setFilterFieldsForQuery(newFilterFields);
    switch (selectedSearchType) {
      case avaiableSearchTypes[0]:
        updateDataByMetaDataSearch(
          selectedContainer,
          latestSearchInput.metadataInput.metaDataKey,
          latestSearchInput.metadataInput.metaDataValue,
          Number(emptyData.EMPTY_OFFSET),
          Number(emptyData.EMPTY_OFFSET),
          selectedLimit,
          latestSearchInput.metadataInput.folder,
          sortField,
          isAscending,
          newFilterFields
        );
        break;
      case avaiableSearchTypes[2]:
        updateDataByFolderPatternSearch(
          selectedContainer,
          Number(emptyData.EMPTY_OFFSET),
          Number(emptyData.EMPTY_OFFSET),
          selectedLimit,
          latestSearchInput.folderPatternInput.folderPattern,
          latestSearchInput.folderPatternInput.fileName,
          sortField,
          isAscending,
          newFilterFields
        );
        break;
      default:
        console.error("Search type not selected");
    }
  };

  const handleSearchTypesDropdownMenuClick = (aSearchType: string) => {
    updateSelectedSearchType(aSearchType);
    setLatestSearchInput(emptyLatestInputState);
    currentUrlSearchParameters.resetSearchTypesProperties();
    removeSearchTypeValueSearchParams(searchParams);
    resetRowSelection();
    setCurrentPagingStack(new PagingStack([]));
  };

  const applyPagingLimit = (limit: number) => {
    if (
      isSearchDone === true &&
      selectedContainer.containerName !== DEFAULT_CONTAINER_LABEL
    ) {
      switch (selectedSearchType) {
        case avaiableSearchTypes[0]:
          //sth metadata
          updateDataByMetaDataSearch(
            selectedContainer,
            latestSearchInput.metadataInput.metaDataKey,
            latestSearchInput.metadataInput.metaDataValue,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit,
            latestSearchInput.metadataInput.folder,
            sortField,
            isAscending,
            filterFieldsForQuery
          );
          break;
        case avaiableSearchTypes[1]:
          //sth gdrive
          updateDataByGDriveSearch(
            selectedContainer,
            latestSearchInput.gdriveInput.GdriveLink,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit
          );
          break;
        case avaiableSearchTypes[2]:
          //sth folder pattern
          updateDataByFolderPatternSearch(
            selectedContainer,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit,
            latestSearchInput.folderPatternInput.folderPattern,
            latestSearchInput.folderPatternInput.fileName,
            sortField,
            isAscending,
            filterFieldsForQuery
          );
          break;
        default:
          console.error("Search type not selected");
      }
    } else {
      setRequestDataQuerry((prevData) =>
        updateRequestDataLimit(prevData, limit)
      );
    }
    setCurrentPagingStack(new PagingStack([]));
  };

  const resetRowSelection = () => {
    setIsFileSelected(false);
    setSelectedRowKeys([]);
  };

  const appInsights = useAppInsightsContext();

  const trackSearchByMetaData = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[
      ClientApiDiagnosticEvents.ClientSearchFilesByMetaData
    ],
    {} as { metaDataKey: string; metaDataValue: string }
  );
  const updateDataByMetaDataSearch = async (
    container: ContainerData,
    metaDataKey: string,
    metaDataValue: string,
    offset: number,
    previousOffset: number,
    limit: number,
    folder?: string,
    sortField?: Field,
    isAscending?: boolean,
    filterFields?: FilterField[]
  ) => {
    if (!filterFields) {
      setFilterFields([
        new FilterField(Field.FileName, Comparison.Like, ""),
        new FilterField(Field.FolderName, Comparison.Like, ""),
        new FilterField(Field.FileSize, Comparison.Equals, ""),
        new FilterField(Field.FileSize, Comparison.Equals, ""),
        new FilterField(Field.ModificationDate, Comparison.Equals, ""),
        new FilterField(Field.ModificationDate, Comparison.Equals, ""),
      ]);
      setFilterFieldsForQuery([]);
    }
    if (!sortField) {
      setSortField(Field.FileName);
      sortField = Field.FileName;
    }
    if (isAscending === undefined) {
      setIsAscending(true);
      isAscending = true;
    }
    setIsSearchDone(false);
    setSearching(true);
    if (!isLocalHost()) {
      trackSearchByMetaData({ metaDataKey, metaDataValue });
    }
    return StorageAPIClient.searchByMetadata(
      container,
      metaDataKey,
      metaDataValue,
      offset,
      previousOffset,
      limit,
      folder,
      sortField,
      isAscending,
      filterFields
    )
      .then((dataQuery) => {
        resetRowSelection();
        setIsSearchDone(true);
        return setRequestDataQuerry(dataQuery);
      })
      .then(() =>
        updateStoredInputData(
          updateSetLatestSearchInput,
          latestSearchInput,
          getMetadataInputValue(metaDataKey, metaDataValue, folder)
        )
      )
      .then(() =>
        currentUrlSearchParameters.setSearchTypesProperties(
          getMetadataInputValue(metaDataKey, metaDataValue, folder)
        )
      )
      .then(() => {
        setSearching(false);
        setIsSearchDone(true);
      })
      .catch((error) => {
        console.error("Error:", error.message);
      });
  };

  const trackFolderPatternSearch = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[
      ClientApiDiagnosticEvents.ClientSearchFilesByFolderPattern
    ],
    {} as { folderPattern?: string; fileName?: string }
  );
  const updateDataByFolderPatternSearch = async (
    container: ContainerData,
    offset: number,
    previousOffset: number,
    limit: number,
    folderPattern?: string,
    fileName?: string,
    sortField?: Field,
    isAscending?: boolean,
    filterFields?: FilterField[]
  ) => {
    if (!filterFields) {
      setFilterFields([
        new FilterField(Field.FileName, Comparison.Like, ""),
        new FilterField(Field.FolderName, Comparison.Like, ""),
        new FilterField(Field.FileSize, Comparison.Equals, ""),
        new FilterField(Field.FileSize, Comparison.Equals, ""),
        new FilterField(Field.ModificationDate, Comparison.Equals, ""),
        new FilterField(Field.ModificationDate, Comparison.Equals, ""),
      ]);
      setFilterFieldsForQuery([]);
    }
    if (!sortField) {
      setSortField(Field.FileName);
      sortField = Field.FileName;
    }
    if (isAscending === undefined) {
      setIsAscending(true);
      isAscending = true;
    }
    setSearching(true);
    setIsSearchDone(false);
    if (!isLocalHost()) {
      trackFolderPatternSearch({ folderPattern, fileName });
    }
    return StorageAPIClient.searchByVirtualFolderPattern(
      container,
      offset,
      previousOffset,
      limit,
      folderPattern,
      fileName,
      sortField,
      isAscending,
      filterFields
    )
      .then((dataQuerry) => {
        setIsSearchDone(true);
        resetRowSelection();
        return setRequestDataQuerry(dataQuerry);
      })
      .then(() =>
        updateStoredInputData(
          updateSetLatestSearchInput,
          latestSearchInput,
          getFolderPatternInputValue(folderPattern, fileName)
        )
      )
      .then(() =>
        currentUrlSearchParameters.setSearchTypesProperties(
          getFolderPatternInputValue(folderPattern, fileName)
        )
      )
      .then(() => {
        setSearching(false);
        setIsSearchDone(true);
      })
      .catch((error) => {
        console.error("Error:", error.message);
      });
  };

  const trackGDriveSearch = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[ClientApiDiagnosticEvents.ClientSearchByGDrive],
    {} as { GdriveLink: string }
  );
  const updateDataByGDriveSearch = (
    container: ContainerData,
    GdriveLink: string,
    offset: number,
    previousOffset: number,
    limit: number
  ) => {
    setIsSearchDone(false);
    setSearching(true);
    if (!isLocalHost()) {
      trackGDriveSearch({ GdriveLink });
    }
    return StorageAPIClient.searchByGdriveLink(
      container,
      GdriveLink,
      offset,
      previousOffset,
      limit
    )
      .then((dataQuerry) => {
        setIsSearchDone(true);
        resetRowSelection();
        return setRequestDataQuerry(dataQuerry);
      })
      .then(() =>
        updateStoredInputData(
          updateSetLatestSearchInput,
          latestSearchInput,
          getGdriveInputValue(GdriveLink)
        )
      )
      .then(() =>
        currentUrlSearchParameters.setSearchTypesProperties(
          getGdriveInputValue(GdriveLink)
        )
      )
      .then(() => {
        setSearching(false);
        setIsSearchDone(true);
      })
      .catch((error) => {
        console.error("Error:", error.message);
      });
  };

  const closePreview = async () => {
    setIsFileSelected(false);
    setSelectedRowKeys([]);
    setSelectedFiles([]);
  };

  //TODO code repetition, the same in applyPagingLimit
  const navigateUsingContinuationToken = async (token: string) => {
    if (token === requestDataQuerry.nextContinuationToken) {
      currentPagingStack.previousTokens.push(
        requestDataQuerry.previousContinuationToken
      );
      if (
        isSearchDone === true &&
        selectedContainer.containerName !== DEFAULT_CONTAINER_LABEL
      ) {
        switch (selectedSearchType) {
          case avaiableSearchTypes[0]:
            //metadata
            await updateDataByMetaDataSearch(
              selectedContainer,
              latestSearchInput.metadataInput.metaDataKey,
              latestSearchInput.metadataInput.metaDataValue,
              Number(token),
              Number(requestDataQuerry.currentContinuationToken),
              selectedLimit,
              latestSearchInput.metadataInput.folder,
              sortField,
              isAscending,
              filterFieldsForQuery
            );
            break;
          case avaiableSearchTypes[1]:
            //gdrive
            await updateDataByGDriveSearch(
              selectedContainer,
              latestSearchInput.gdriveInput.GdriveLink,
              Number(token),
              Number(requestDataQuerry.currentContinuationToken),
              selectedLimit
            );
            break;
          case avaiableSearchTypes[2]:
            //folder pattern
            await updateDataByFolderPatternSearch(
              selectedContainer,
              Number(token),
              Number(requestDataQuerry.currentContinuationToken),
              selectedLimit,
              latestSearchInput.folderPatternInput.folderPattern,
              latestSearchInput.folderPatternInput.fileName,
              sortField,
              isAscending,
              filterFieldsForQuery
            );
            break;
          default:
            console.error("Search type not selected");
        }
      }
    }
    if (token === requestDataQuerry.previousContinuationToken) {
      if (
        isSearchDone === true &&
        selectedContainer.containerName !== DEFAULT_CONTAINER_LABEL
      ) {
        switch (selectedSearchType) {
          case avaiableSearchTypes[0]:
            //sth metadata
            await updateDataByMetaDataSearch(
              selectedContainer,
              latestSearchInput.metadataInput.metaDataKey,
              latestSearchInput.metadataInput.metaDataValue,
              Number(token),
              Number(
                currentPagingStack.previousTokens[
                  currentPagingStack.previousTokens.length - 1
                ]
              ),
              selectedLimit,
              latestSearchInput.metadataInput.folder,
              sortField,
              isAscending,
              filterFieldsForQuery
            );
            break;
          case avaiableSearchTypes[1]:
            //sth gdrive
            await updateDataByGDriveSearch(
              selectedContainer,
              latestSearchInput.gdriveInput.GdriveLink,
              Number(token),
              Number(
                currentPagingStack.previousTokens[
                  currentPagingStack.previousTokens.length - 1
                ]
              ),
              selectedLimit
            );
            break;
          case avaiableSearchTypes[2]:
            //sth folder pattern
            await updateDataByFolderPatternSearch(
              selectedContainer,
              Number(token),
              Number(
                currentPagingStack.previousTokens[
                  currentPagingStack.previousTokens.length - 1
                ]
              ),
              selectedLimit,
              latestSearchInput.folderPatternInput.folderPattern,
              latestSearchInput.folderPatternInput.fileName,
              sortField,
              isAscending,
              filterFieldsForQuery
            );
            break;
          default:
            console.error("Search type not selected");
        }
      }
      currentPagingStack.previousTokens.pop();
    }
  };

  const loadBrowseSessionFromUrl = async (
    urlContainer: string,
    urlSearchType: string,
    containersList: string[]
  ) => {
    setLoadingComponent(true);
    setSelectedSearchType(
      avaiableSearchTypes.includes(urlSearchType)
        ? urlSearchType
        : DEFAULT_SEARCH_LABEL
    );
    const limit =
      currentUrlSearchParameters.limit !== selectedLimit.toString() &&
      !isNaN(Number(currentUrlSearchParameters.limit))
        ? Number(currentUrlSearchParameters.limit)
        : selectedLimit;
    setSelectedLimit(limit);
    if (containersList.includes(urlContainer)) {
      const container = await updateContainer(urlContainer);

      switch (urlSearchType) {
        case avaiableSearchTypes[0]:
          //metadata
          updateDataByMetaDataSearch(
            container,
            currentUrlSearchParameters.metadataParams?.metaDataKey as string,
            currentUrlSearchParameters.metadataParams?.metaDataValue as string,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit,
            currentUrlSearchParameters.metadataParams?.folder
          );
          break;
        case avaiableSearchTypes[1]:
          //gdrive
          updateDataByGDriveSearch(
            container,
            currentUrlSearchParameters.gdriveParams?.GdriveLink as string,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit
          );
          break;
        case avaiableSearchTypes[2]:
          //folder pattern
          updateDataByFolderPatternSearch(
            container,
            Number(emptyData.EMPTY_OFFSET),
            Number(emptyData.EMPTY_OFFSET),
            limit,
            currentUrlSearchParameters.folderPatternParams
              ?.folderPattern as string,
            currentUrlSearchParameters.folderPatternParams?.fileName as string
          );
          break;
        default:
          console.error("Search type not selected");
      }
      setLoadingComponent(false);
      return true;
    } else {
      setLoadingComponent(false);
      return false;
    }
  };

  const isUrlValid = (searchParams: URLSearchParams) => {
    if (searchParams.toString() === "") {
      return false;
    }
    if (
      !searchParams.get("container") ||
      !searchParams.get("search") ||
      searchParams.get("container") === (DEFAULT_CONTAINER_LABEL || "") ||
      searchParams.get("search") === (DEFAULT_SEARCH_LABEL || "")
    ) {
      return false;
    }
    return true;
  };

  const navigateWithSearchParams = (
    oldSearchParams: URLSearchParams,
    searchParams: URLSearchParams
  ) => {
    const oldSearch = oldSearchParams.toString();
    const newSearch = searchParams.toString();
    if (
      isUrlValid(oldSearchParams) &&
      oldSearch !== newSearch &&
      wasChangeSignificant(oldSearchParams, searchParams)
    ) {
      navigate({
        pathname: "",
        search: newSearch,
      });
    } else {
      navigate(
        {
          pathname: "",
          search: newSearch,
        },
        { replace: true }
      );
    }
  };

  useEffect(() => {
    let urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString() === "") {
      setForceReload(!forceReload);
      setIsSearchDone(false);
    }
  }, [window.location.search]);

  useEffect(() => {
    if (
      containerWriteData.containers
        .map((container) => container.containerName)
        .includes(selectedContainer.containerName)
    ) {
      setContainerWriteAccess(true);
    } else {
      setContainerWriteAccess(false);
    }
  }, [selectedContainer, containerWriteData]);

  useEffect(() => {
    let files: File[] = [];
    for (let i = 0; i < selectedRowKeys.length; i++) {
      if (!isNaN(Number(selectedRowKeys[i]))) {
        files = [...files, requestDataQuerry.files[Number(selectedRowKeys[i])]];
      }
    }
    setSelectedFiles(files);
  }, [selectedRowKeys]);

  const firstRenderForUseEffect = useRef(true);
  const firstRenderForPopstate = useRef(true);
  const popstateHistoryBlocker = useRef(false);
  useEffect(() => {
    if (firstRenderForUseEffect.current) {
      firstRenderForUseEffect.current = false;
    } else {
      let oldParams = searchParams.toString();
      searchParams.set(
        "container",
        requestDataQuerry.containerName !== DEFAULT_CONTAINER_LABEL
          ? selectedContainer.containerName
          : ""
      );
      searchParams.set(
        "search",
        selectedSearchType === DEFAULT_SEARCH_LABEL ? "" : selectedSearchType
      );
      if (selectedSearchType) {
        updateUrlWithSelectedSearch(searchParams, latestSearchInput);
      }
      searchParams.set("limit", requestDataQuerry.limit.toString());
      currentUrlSearchParameters.updateSearchParamsByUrl(searchParams);
      if (!popstateHistoryBlocker.current) {
        navigateWithSearchParams(new URLSearchParams(oldParams), searchParams);
      } else {
        popstateHistoryBlocker.current = false;
      }
      firstRenderForPopstate.current = false;
    }
  }, [
    requestDataQuerry,
    selectedContainer,
    selectedSearchType,
    latestSearchInput,
  ]);

  //on component mounting do:
  useEffect(() => {
    getContainerList("Read")
      .then((containersList) => {
        const containersData: ContainerData[] = containersList.map((name) => ({
          containerName: name,
          folderStructure: new ContainerFolderStructure(""),
          metadataTemplate: [],
        }));

        setContainerData((prevData) => ({
          ...prevData,
          containers: containersData,
        }));
        return containersList;
      })
      .then((containersList) => {
        currentUrlSearchParameters.updateSearchParamsByUrl(searchParams);
        loadBrowseSessionFromUrl(
          currentUrlSearchParameters.container,
          currentUrlSearchParameters.search,
          containersList
        )
          .then((checkValidUrlParams) => {
            let container = currentUrlSearchParameters.container;
            if (!checkValidUrlParams) {
              //localStorage has priority over first container from the list, overriden if null
              const container = containerState
                ? containerState
                : containersList[0];
              updateContainer(container);
            }
            return container;
          })
          .then((container) => {
            getContainerList("Write")
              .then((containersWriteList) => {
                const containerWriteData: ContainerData[] =
                  containersWriteList.map((name) => ({
                    containerName: name,
                    folderStructure: new ContainerFolderStructure(""),
                    metadataTemplate: [],
                  }));
                setContainerWriteData((prevData) => ({
                  ...prevData,
                  containers: containerWriteData,
                }));

                return containersWriteList;
              })
              .then((containersWriteList) => {
                if (containersWriteList.indexOf(container) !== -1) {
                  setContainerWriteAccess(true);
                } else {
                  setContainerWriteAccess(false);
                }
              });
          });
      })
      .catch((error) => {
        console.error("Error on mount:", error.message);
      });
  }, [forceReload]); //empty dependency array ensures one execution, ignore warning

  const updateFileBrowserPagingLimit = async (limit: number) => {
    setSelectedLimit(limit);
    applyPagingLimit(limit);
  };

  window.onpopstate = async function handleNavigation() {
    const url = new URL(window.location.href);
    if (!firstRenderForPopstate.current) {
      const urlParams = new URLSearchParams(url.searchParams);
      currentUrlSearchParameters.updateSearchParamsByUrl(searchParams);
      loadBrowseSessionFromUrl(
        currentUrlSearchParameters.container,
        currentUrlSearchParameters.search,
        containerData.containers.map((item) => item.containerName)
      ).then(() => {
        popstateHistoryBlocker.current = true;
      });
      firstRenderForPopstate.current = false;
    } else {
      const urlParams = new URLSearchParams(url.searchParams);
      currentUrlSearchParameters.updateSearchParamsByUrl(urlParams);
      popstateHistoryBlocker.current = true;
      firstRenderForPopstate.current = false;
    }
  };
  let resultGridHeight: string = "calc(100vh - 450px)";
  return (
    <div className="searchPageDiv">
      {containerData.containers.length === 0 && (
        <Overlay
          noAccesText="You don't have permissions to any containers."
        />
      )}
      {loadingComponent && (
        <Overlay noAccesText="Loading..." />
      )}
      <Row align="top">
        <Col flex={1}>
          <Row className="gsk-sub-header-menu">
            <Col className="containerText">Container :</Col>
            <Col className="containerDropdown">
              <DropdownListSelection
                theme={theme}
                optionsList={containerData.containers.map(
                  (containerData) => containerData.containerName
                )}
                onMenuClick={handleContainersDropdownMenuClick}
                placeholderText={selectedContainer.containerName}
              />
            </Col>
            <Col className="searchType">Search type :</Col>
            <Col style={{ flex: 1 }} className="searchDropdown">
              <DropdownListSelection
                theme={theme}
                optionsList={avaiableSearchTypes}
                onMenuClick={handleSearchTypesDropdownMenuClick}
                placeholderText={selectedSearchType}
              />
            </Col>
            <Col className="pageLimit">Select page limit :</Col>
            <Col className="pageLimitDropdown">
              <DropdownListSelection
                theme={theme}
                optionsList={limit_options.map(String)}
                onMenuClick={(label) =>
                  updateFileBrowserPagingLimit(Number(label))
                }
                placeholderText={String(selectedLimit)}
              />
            </Col>
          </Row>
          <div className="selected-search-type-div">
            {(() => {
              switch (selectedSearchType) {
                case avaiableSearchTypes[0]:
                  resultGridHeight = "calc(100vh - 450px)";
                  return (
                    <MetadataSearch
                      theme={theme}
                      sendDataComponent={updateDataByMetaDataSearch}
                      limit={selectedLimit}
                      selectedContainer={selectedContainer}
                      metadataFromUrl={latestSearchInput.metadataInput}
                    />
                  );
                case avaiableSearchTypes[1]:
                  resultGridHeight = "calc(100vh - 350px)";
                  return (
                    <GdriveSearch
                      theme={theme}
                      sendDataComponent={updateDataByGDriveSearch}
                      limit={selectedLimit}
                      selectedContainer={selectedContainer}
                      GdrivefromUrl={latestSearchInput.gdriveInput}
                    />
                  );
                case avaiableSearchTypes[2]:
                  resultGridHeight = "calc(100vh - 450px)";
                  return (
                    <FolderPatternSearch
                      theme={theme}
                      sendDataComponent={updateDataByFolderPatternSearch}
                      limit={selectedLimit}
                      selectedContainer={selectedContainer}
                      folderFromUrl={latestSearchInput.folderPatternInput}
                    />
                  );
                default:
                  return (
                    <Col span={24}>
                      <h3 className="unknown-search-type">
                        Please select type of search
                      </h3>
                    </Col>
                  );
              }
            })()}
          </div>
          {isSearchDone && (
            <div
              className="resultsDiv"
              style={{
                marginTop: "0px",
              }}
            >
              <Row gutter={0}>
                {selectedContainer.containerName !==
                  DEFAULT_CONTAINER_LABEL && (
                  <Col span={selectedFiles.length > 0 ? 18 : 24}>
                    <FileBrowsingTable
                      selectedSearchType={selectedSearchType}
                      theme={theme}
                      filterOn={true}
                      dataQuerry={requestDataQuerry}
                      onFolderClick={() => {}}
                      selectedRowKeys={selectedRowKeys}
                      setSelectedRowKeys={(key: React.Key[]) =>
                        setSelectedRowKeys(key)
                      }
                      continuationTokenNavigation={
                        navigateUsingContinuationToken
                      }
                      showFolderColumn={true}
                      showSelectFolderAction={false}
                      onSelectFolderClick={() => {}}
                      previousTokens={currentPagingStack.previousTokens}
                      ySizeVH={resultGridHeight}
                      sendFilterAndSorting={updateSortField}
                      filterFieldsFromParent={filterFields}
                      sizesWithUnitFromParent={sizesWithUnit}
                    />
                  </Col>
                )}
                {selectedContainer.containerName ===
                  DEFAULT_CONTAINER_LABEL && (
                  <Col span={24}>
                    <p className="containerSelectionText">Select a container</p>
                  </Col>
                )}
                {selectedFiles.length > 0 && (
                  <>
                    <Col span={6}>
                      <MetadataPreview
                        theme={theme}
                        selectedContainer={selectedContainer}
                        selectedFiles={selectedFiles}
                        closePreview={closePreview}
                        containerAccess={containerWriteAccess}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </div>
          )}
          {searching &&<div className="loadingTable"><p className="overlayText">Loading...</p></div>}
        </Col>
      </Row>
    </div>
  );
};
